import axios from 'axios'
import React, { useEffect, useState, useRef } from 'react'
import { rootUrl, formatDate, formatToZMW, handlePrint, handleExcel } from '../../../helpers'
import { useAuth } from '../../../context/auth-context'
import { toast } from 'react-toastify';
import { FaSpinner, FaDownload, FaPrint, FaChartLine, FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaMapMarkerAlt, FaCalendarAlt, FaMoneyBillWave } from 'react-icons/fa';
import Select from 'react-select';

export default function BillReport({startDate, endDate,triggerFetch}) {

    const auth = useAuth();
    const [bills, setBills] = useState([])
    const [filtered, setFiltered] = useState([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('summary')
    const [properties, setProperties] = useState([])
    const [propertiesLoading, setPropertiesLoading] = useState(false)
    const [deduplicationStats, setDeduplicationStats] = useState({ originalBills: 0, uniqueBills: 0, originalProperties: 0, uniqueProperties: 0 })
    const printRef = useRef()

    const fetchData = async () =>{
        try{
            const data =  await axios.get(rootUrl(`/getBillsDate/${auth.user.cid}~${startDate}~${endDate}`))
            
            // Filter out records with null/empty property_no or location
            const validBills = data.data.data.filter(bill => 
                bill.property_no && 
                bill.property_no.trim() !== '' && 
                bill.location && 
                bill.location.trim() !== ''
            );
            
            setBills(validBills)
            setFiltered(validBills)
            setDeduplicationStats(prev => ({ ...prev, originalBills: data.data.data.length, uniqueBills: validBills.length }))
            setLoading(false)
        }catch(e){
            toast.error(e?.message)
        }
    }

    const fetchProperties = async () => {
        try {
            setPropertiesLoading(true)
            const data = await axios.get(rootUrl(`/getProperties/${auth.user.id}`))
            
            // First filter out records with null/empty property_no or location
            const validProperties = data.data.data.filter(property => 
                property.property_no && 
                property.property_no.trim() !== '' && 
                property.location && 
                property.location.trim() !== ''
            );
            
            // Then deduplicate properties by property_no, keeping the most recent one
            const uniqueProperties = validProperties.reduce((acc, property) => {
                const existingProperty = acc.find(p => p.property_no === property.property_no);
                if (!existingProperty) {
                    acc.push(property);
                } else {
                    // Keep the property with the higher ID (more recent) or better data
                    if (property.id > existingProperty.id || 
                        (property.annual_payable && !existingProperty.annual_payable)) {
                        const index = acc.findIndex(p => p.property_no === property.property_no);
                        acc[index] = property;
                    }
                }
                return acc;
            }, []);
            
            setProperties(uniqueProperties)
            setDeduplicationStats(prev => ({ ...prev, originalProperties: data.data.data.length, uniqueProperties: uniqueProperties.length }))
            setPropertiesLoading(false)
        } catch(e) {
            toast.error(e?.message)
            setPropertiesLoading(false)
        }
    }


    useEffect(()=>{
        if(triggerFetch && startDate && endDate){
            setLoading(true)
            fetchData()
            fetchProperties()
        }
    },[triggerFetch])

   const filterLocation = (location) =>{
  
    if(location === '' || location === undefined){
      setFiltered(bills)
    }else{
      setFiltered(bills.filter(a=>a.location === location))
    }
  
   }

   const locationOptions = [...new Set(bills.map(a => a.location))]
    .map(location => ({ value: location, label: location }));

  // Analytics functions
  const getSummaryMetrics = () => {
    const totalBills = filtered.reduce((sum, row) => sum + (parseFloat(row.tt) || 0), 0);
    const totalReceipts = filtered.reduce((sum, row) => sum + (parseFloat(row.tr) || 0), 0);
    const totalOutstanding = filtered.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
    const collectionRate = totalBills > 0 ? ((totalReceipts / totalBills) * 100).toFixed(1) : 0;
    const totalProperties = filtered.length;
    const payingProperties = filtered.filter(row => parseFloat(row.tr) > 0).length;
    const nonPayingProperties = totalProperties - payingProperties;
    
    return {
      totalBills,
      totalReceipts,
      totalOutstanding,
      collectionRate,
      totalProperties,
      payingProperties,
      nonPayingProperties
    };
  };

  const getLocationBreakdown = () => {
    const locationData = {};
    filtered.forEach(row => {
      const location = row.location || 'Unknown';
      if (!locationData[location]) {
        locationData[location] = {
          totalBills: 0,
          totalReceipts: 0,
          totalOutstanding: 0,
          propertyCount: 0,
          payingCount: 0
        };
      }
      locationData[location].totalBills += parseFloat(row.tt) || 0;
      locationData[location].totalReceipts += parseFloat(row.tr) || 0;
      locationData[location].totalOutstanding += parseFloat(row.amount) || 0;
      locationData[location].propertyCount += 1;
      if (parseFloat(row.tr) > 0) {
        locationData[location].payingCount += 1;
      }
    });
    return locationData;
  };

  const getPaymentStatusAnalysis = () => {
    const paying = filtered.filter(row => parseFloat(row.tr) > 0);
    
    const partiallyPaid = filtered.filter(row => parseFloat(row.tr) > 0 && parseFloat(row.amount) > 0);
    const fullyPaid = filtered.filter(row => parseFloat(row.amount) == 0 && parseFloat(row.tr) > 0);
    const nonPaying = filtered.filter(row => !paying.includes(row) && !partiallyPaid.includes(row) && !fullyPaid.includes(row));
  
    
    return { paying, nonPaying, partiallyPaid, fullyPaid };
  };

  const getAgingAnalysis = () => {
    const now = new Date();
    const aging = {
      current: 0,
      '30days': 0,
      '60days': 0,
      '90days': 0,
      'over90days': 0
    };
    
    filtered.forEach(row => {
      const billDate = new Date(row.date);
      const daysDiff = Math.floor((now - billDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff <= 30) aging.current += parseFloat(row.amount) || 0;
      else if (daysDiff <= 60) aging['30days'] += parseFloat(row.amount) || 0;
      else if (daysDiff <= 90) aging['60days'] += parseFloat(row.amount) || 0;
      else if (daysDiff <= 120) aging['90days'] += parseFloat(row.amount) || 0;
      else aging.over90days += parseFloat(row.amount) || 0;
    });
    
    return aging;
  };

  const metrics = getSummaryMetrics();
  const locationBreakdown = getLocationBreakdown();
  const paymentStatus = getPaymentStatusAnalysis();
  const agingAnalysis = getAgingAnalysis();

  const tabs = [
    { id: 'summary', label: 'Executive Summary', icon: FaChartLine },
    { id: 'payments', label: 'Payment Analysis', icon: FaMoneyBillWave },
    { id: 'locations', label: 'Location Breakdown', icon: FaMapMarkerAlt },
    { id: 'aging', label: 'Aging Analysis', icon: FaCalendarAlt },
    { id: 'detailed', label: 'Detailed Bills', icon: FaExclamationTriangle }
  ];

  return (
    <div ref={printRef} className="w-full space-y-6">
      {loading ? (
        <div className='flex flex-col justify-center items-center h-64 w-full bg-white rounded-2xl border border-gray-100 shadow-sm'>
          <FaSpinner size={50} className="animate-spin text-blue-950 mb-4" />
          <p className="text-gray-600 text-lg">Loading bills report...</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Header with Export Options */}
            <div className=" px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-950 rounded-lg p-2">
                    <FaChartLine className="text-white text-xl" />
                  </div>
                  <div>
                    <h2 className='font-bold text-xl'>Bills & Properties Report</h2>
                    <p className="text-gray-600 text-sm">Comprehensive billing and property analysis</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePrint(printRef)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors shadow-sm"
                    title="Print Report"
                  >
                    <FaPrint /> <span className="hidden md:inline">Print</span>
                  </button>
                  <button
                    onClick={() => handleExcel(printRef, `Bills_Report_${startDate}_${endDate}`)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow-sm"
                    title="Export to Excel"
                  >
                    <FaDownload /> <span className="hidden md:inline">Excel</span>
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <FaCalendarAlt />
                <span>Period: {formatDate(startDate)} - {formatDate(endDate)}</span>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Location Filter */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <FaMapMarkerAlt className="text-blue-950" />
                  <h3 className="font-semibold text-gray-800">Location Filter</h3>
                </div>
                <Select
                  options={locationOptions}
                  onChange={(selectedOption) => filterLocation(selectedOption?.value)}
                  placeholder="Filter by location"
                  isClearable
                  className="w-full md:w-[300px]"
                />
              </div>

              {/* Tab Navigation */}
              <div className="flex flex-wrap gap-2 border-b border-gray-200">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-t-lg font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white border-b-2 border-blue-400 shadow-sm scale-105'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <tab.icon /> {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === 'summary' && <SummaryDashboard metrics={metrics} deduplicationStats={deduplicationStats} />}
              {activeTab === 'payments' && <PaymentAnalysis paymentStatus={paymentStatus} metrics={metrics} />}
              {activeTab === 'locations' && <LocationBreakdown locationBreakdown={locationBreakdown} />}
              {activeTab === 'aging' && <AgingAnalysis agingAnalysis={agingAnalysis} />}
              {activeTab === 'detailed' && <TableComponent data={filtered} />}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const TableComponent = ({ data }) => {
  const columns = [
    { name: "Sn", selector: (row, index) => index + 1 },
    { name: "Property", selector: (row) => row?.property_no },
    { name: "Customer", selector: (row) => `${row?.name}` },
    { name: "Description", selector: (row) => `${row?.description}` },
    { name: "Location", selector: (row) => row?.location },
    { name: "Bill", selector: (row) => formatToZMW(parseFloat(row.tt) || 0) },
    { name: "Receipt", selector: (row) => formatToZMW(parseFloat(row.tr) || 0) },
    { name: "Outstanding", selector: (row) => formatToZMW(parseFloat(row.amount) || 0) },
  ];

  const totalBill = data.reduce((sum, row) => sum + (parseFloat(row.tt) || 0), 0);
  const totalReceipt = data.reduce((sum, row) => sum + (parseFloat(row.tr) || 0), 0);
  const totalOutstanding = data.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full">
        <thead>
          <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
            {columns.map((col, index) => (
              <th key={index} className="py-3 px-4 text-left font-semibold border-r border-blue-700">{col.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={row.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
              {columns.map((col, colIndex) => (
                <td key={colIndex} className="py-3 px-4 border-r border-gray-100 text-gray-700">
                  {col.selector(row, rowIndex)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {/* Totals Row */}
        <tfoot>
          <tr className="bg-gradient-to-r from-gray-700 to-gray-800 text-white font-bold border-t-2 border-gray-400">
            <td colSpan="5" className="py-3 px-4 text-right border-r border-gray-600 flex items-center gap-2">
              <FaMoneyBillWave />
              Total
            </td>
            <td className="py-3 px-4 border-r border-gray-600 text-lg">{formatToZMW(totalBill)}</td>
            <td className="py-3 px-4 border-r border-gray-600 text-lg text-green-300">{formatToZMW(totalReceipt)}</td>
            <td className="py-3 px-4 text-lg text-orange-300">{formatToZMW(totalOutstanding)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// Summary Dashboard Component
const SummaryDashboard = ({ metrics, deduplicationStats }) => {
  const summaryCards = [
    {
      title: 'Total Properties',
      value: metrics.totalProperties,
      icon: FaMapMarkerAlt,
      color: 'bg-blue-500',
      subtitle: 'Properties in system'
    },
    {
      title: 'Paying Properties',
      value: metrics.payingProperties,
      icon: FaCheckCircle,
      color: 'bg-green-500',
      subtitle: `${((metrics.payingProperties / metrics.totalProperties) * 100).toFixed(1)}% of total`
    },
    {
      title: 'Non-Paying Properties',
      value: metrics.nonPayingProperties,
      icon: FaTimesCircle,
      color: 'bg-red-500',
      subtitle: `${((metrics.nonPayingProperties / metrics.totalProperties) * 100).toFixed(1)}% of total`
    },
    {
      title: 'Total Bills Generated',
      value: formatToZMW(metrics.totalBills),
      icon: FaMoneyBillWave,
      color: 'bg-purple-500',
      subtitle: 'Amount billed'
    },
    {
      title: 'Total Collections',
      value: formatToZMW(metrics.totalReceipts),
      icon: FaCheckCircle,
      color: 'bg-green-600',
      subtitle: 'Amount collected'
    },
    {
      title: 'Outstanding Amount',
      value: formatToZMW(metrics.totalOutstanding),
      icon: FaExclamationTriangle,
      color: 'bg-orange-500',
      subtitle: 'Amount pending'
    },
    {
      title: 'Collection Rate',
      value: `${metrics.collectionRate}%`,
      icon: FaChartLine,
      color: metrics.collectionRate >= 70 ? 'bg-green-500' : metrics.collectionRate >= 50 ? 'bg-yellow-500' : 'bg-red-500',
      subtitle: 'Payment efficiency'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {summaryCards.map((card, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow" style={{ borderLeftColor: card.color.replace('bg-', '') }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                <p className="text-xs text-gray-500 mt-2">{card.subtitle}</p>
              </div>
              <div className={`p-3 rounded-full ${card.color} text-white shadow-sm`}>
                <card.icon className="h-6 w-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Data Quality & Deduplication */}
      {/* {deduplicationStats.originalProperties > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-4">Data Quality Report</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Bills Data</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Original Records:</span>
                  <span className="font-medium">{deduplicationStats.originalBills}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Valid Records:</span>
                  <span className="font-medium text-green-600">{deduplicationStats.uniqueBills}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Filtered Out:</span>
                  <span className="font-medium text-orange-600">{deduplicationStats.originalBills - deduplicationStats.uniqueBills}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Note:</span>
                  <span className="text-xs text-gray-500">Removed null property_no or location</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Properties Processing</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Original Records:</span>
                  <span className="font-medium">{deduplicationStats.originalProperties}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Valid & Unique:</span>
                  <span className="font-medium text-green-600">{deduplicationStats.uniqueProperties}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Filtered & Deduplicated:</span>
                  <span className="font-medium text-orange-600">{deduplicationStats.originalProperties - deduplicationStats.uniqueProperties}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Note:</span>
                  <span className="text-xs text-gray-500">Removed null values + duplicates</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Data Quality Processing:</strong> Records with null or empty property_no or location have been filtered out. 
              For properties, duplicates (by property_no) are also removed, keeping the most complete record. 
              All valid bills are retained as properties can be billed multiple times per year.
            </p>
          </div>
        </div>
      )} */}

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Key Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Payment Performance</h4>
            <p className="text-sm text-gray-600">
              {metrics.collectionRate >= 70 
                ? `Excellent collection rate of ${metrics.collectionRate}% indicates strong payment compliance.`
                : metrics.collectionRate >= 50 
                ? `Moderate collection rate of ${metrics.collectionRate}% suggests room for improvement in payment follow-up.`
                : `Low collection rate of ${metrics.collectionRate}% requires immediate attention and payment recovery strategies.`
              }
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Property Compliance</h4>
            <p className="text-sm text-gray-600">
              {metrics.payingProperties > metrics.nonPayingProperties
                ? `${metrics.payingProperties} properties are compliant with payments, representing a healthy payment culture.`
                : `${metrics.nonPayingProperties} properties require immediate attention for payment compliance.`
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Analysis Component
const PaymentAnalysis = ({ paymentStatus, metrics }) => {
  const paymentCategories = [
    {
      title: 'Fully Paid Properties',
      data: paymentStatus.fullyPaid,
      color: 'bg-green-500',
      icon: FaCheckCircle,
      description: 'Properties with complete payment'
    },
    {
      title: 'Partially Paid Properties',
      data: paymentStatus.partiallyPaid,
      color: 'bg-yellow-500',
      icon: FaExclamationTriangle,
      description: 'Properties with partial payment'
    },
    {
      title: 'Non-Paying Properties',
      data: paymentStatus.nonPaying,
      color: 'bg-red-500',
      icon: FaTimesCircle,
      description: 'Properties with no payments'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {paymentCategories.map((category, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-full ${category.color} text-white`}>
                <category.icon className="h-6 w-6" />
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-900">{category.data.length}</p>
                <p className="text-sm text-gray-600">Properties</p>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{category.title}</h3>
            <p className="text-sm text-gray-600 mb-4">{category.description}</p>
            
            {category.data.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Sample Properties:</p>
                <div className="max-h-32 overflow-y-auto">
                  {category.data.slice(0, 5).map((property, idx) => (
                    <div key={idx} className="text-xs text-gray-600 py-1 border-b border-gray-100">
                      {property.property_no} - {property.name}
                    </div>
                  ))}
                  {category.data.length > 5 && (
                    <p className="text-xs text-gray-500 mt-2">... and {category.data.length - 5} more</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Payment Summary Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Payment Summary by Category</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Percentage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Bills</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Receipts</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outstanding</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paymentCategories.map((category, index) => {
                const totalBills = category.data.reduce((sum, row) => sum + (parseFloat(row.tt) || 0), 0);
                const totalReceipts = category.data.reduce((sum, row) => sum + (parseFloat(row.tr) || 0), 0);
                const totalOutstanding = category.data.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
                const percentage = metrics.totalProperties > 0 ? ((category.data.length / metrics.totalProperties) * 100).toFixed(1) : 0;
                
                return (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${category.color} mr-3`}></div>
                        <span className="text-sm font-medium text-gray-900">{category.title}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{category.data.length}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{percentage}%</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatToZMW(totalBills)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatToZMW(totalReceipts)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatToZMW(totalOutstanding)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Location Breakdown Component
const LocationBreakdown = ({ locationBreakdown }) => {
  const locations = Object.entries(locationBreakdown).map(([location, data]) => ({
    location,
    ...data,
    collectionRate: data.totalBills > 0 ? ((data.totalReceipts / data.totalBills) * 100).toFixed(1) : 0,
    paymentRate: data.propertyCount > 0 ? ((data.payingCount / data.propertyCount) * 100).toFixed(1) : 0
  })).sort((a, b) => b.totalBills - a.totalBills);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {locations.map((location, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{location.location}</h3>
              <div className="flex items-center space-x-2">
                <FaMapMarkerAlt className="text-blue-500" />
                <span className="text-sm text-gray-600">{location.propertyCount} properties</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{formatToZMW(location.totalBills)}</p>
                <p className="text-xs text-gray-600">Total Bills</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{formatToZMW(location.totalReceipts)}</p>
                <p className="text-xs text-gray-600">Collections</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Collection Rate</span>
                <span className="font-medium">{location.collectionRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full" 
                  style={{ width: `${Math.min(location.collectionRate, 100)}%` }}
                ></div>
              </div>
              
              <div className="flex justify-between text-sm">
                <span>Payment Compliance</span>
                <span className="font-medium">{location.paymentRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full" 
                  style={{ width: `${Math.min(location.paymentRate, 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Outstanding: {formatToZMW(location.totalOutstanding)}</span>
                <span>Paying: {location.payingCount}/{location.propertyCount}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Location Summary Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Location Performance Summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Properties</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paying</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Bills</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collections</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outstanding</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collection Rate</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {locations.map((location, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{location.location}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{location.propertyCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{location.payingCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatToZMW(location.totalBills)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatToZMW(location.totalReceipts)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatToZMW(location.totalOutstanding)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      location.collectionRate >= 70 ? 'bg-green-100 text-green-800' :
                      location.collectionRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {location.collectionRate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Aging Analysis Component
const AgingAnalysis = ({ agingAnalysis }) => {
  const agingCategories = [
    { label: 'Current (0-30 days)', value: agingAnalysis.current, color: 'bg-green-500' },
    { label: '31-60 days', value: agingAnalysis['30days'], color: 'bg-yellow-500' },
    { label: '61-90 days', value: agingAnalysis['60days'], color: 'bg-orange-500' },
    { label: '91-120 days', value: agingAnalysis['90days'], color: 'bg-red-500' },
    { label: 'Over 120 days', value: agingAnalysis.over90days, color: 'bg-red-700' }
  ];

  const totalOutstanding = Object.values(agingAnalysis).reduce((sum, value) => sum + value, 0);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Outstanding Amount by Age</h3>
        
        <div className="space-y-4">
          {agingCategories.map((category, index) => {
            const percentage = totalOutstanding > 0 ? ((category.value / totalOutstanding) * 100).toFixed(1) : 0;
            return (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">{category.label}</span>
                  <div className="text-right">
                    <span className="text-sm font-bold text-gray-900">{formatToZMW(category.value)}</span>
                    <span className="text-xs text-gray-500 ml-2">({percentage}%)</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full ${category.color}`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Aging Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
          <div className="flex items-center">
            <FaCheckCircle className="text-green-500 h-6 w-6 mr-3" />
            <div>
              <p className="text-sm font-medium text-green-800">Current (0-30 days)</p>
              <p className="text-2xl font-bold text-green-900">{formatToZMW(agingAnalysis.current)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-500">
          <div className="flex items-center">
            <FaExclamationTriangle className="text-yellow-500 h-6 w-6 mr-3" />
            <div>
              <p className="text-sm font-medium text-yellow-800">31-90 days</p>
              <p className="text-2xl font-bold text-yellow-900">{formatToZMW(agingAnalysis['30days'] + agingAnalysis['60days'])}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-500">
          <div className="flex items-center">
            <FaTimesCircle className="text-red-500 h-6 w-6 mr-3" />
            <div>
              <p className="text-sm font-medium text-red-800">Over 90 days</p>
              <p className="text-2xl font-bold text-red-900">{formatToZMW(agingAnalysis['90days'] + agingAnalysis.over90days)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Collection Recommendations</h3>
        <div className="space-y-3">
          {agingAnalysis.over90days > 0 && (
            <div className="flex items-start">
              <FaExclamationTriangle className="text-red-500 h-5 w-5 mr-3 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Immediate Action Required</p>
                <p className="text-sm text-red-700">
                  {formatToZMW(agingAnalysis.over90days)} in outstanding amounts over 90 days old. 
                  Consider legal action or payment plans for these accounts.
                </p>
              </div>
            </div>
          )}
          
          {agingAnalysis['60days'] > 0 && (
            <div className="flex items-start">
              <FaExclamationTriangle className="text-orange-500 h-5 w-5 mr-3 mt-0.5" />
              <div>
                <p className="font-medium text-orange-800">Follow-up Required</p>
                <p className="text-sm text-orange-700">
                  {formatToZMW(agingAnalysis['60days'])} in amounts 61-90 days old. 
                  Send reminder notices and consider payment arrangements.
                </p>
              </div>
            </div>
          )}
          
          {agingAnalysis['30days'] > 0 && (
            <div className="flex items-start">
              <FaCheckCircle className="text-yellow-500 h-5 w-5 mr-3 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Gentle Reminder</p>
                <p className="text-sm text-yellow-700">
                  {formatToZMW(agingAnalysis['30days'])} in amounts 31-60 days old. 
                  Send friendly payment reminders.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

  
